/**
 * Pure habit helpers shared by the Habits board and its phone "Today" mode:
 * lifecycle bounds, the last-7-days sparkline and the streak.
 *
 * Dates are ISO `YYYY-MM-DD` strings, compared as strings and shifted in UTC so
 * the viewer's time zone never moves them.
 */
import type { Habit, HabitLogStatus } from './types';

export const SPARKLINE_DAYS = 7;

/** Looks up the log status of a habit on a date. */
export type HabitStatusLookup = (habit: Habit, date: string) => HabitLogStatus | undefined;

export function shiftDate(date: string, amount: number): string {
  const [year, month, day] = date.split('-').map(Number);
  return new Date(Date.UTC(year, month - 1, day + amount)).toISOString().slice(0, 10);
}

export function daysBetween(from: string, to: string): number {
  const [fromYear, fromMonth, fromDay] = from.split('-').map(Number);
  const [toYear, toMonth, toDay] = to.split('-').map(Number);
  return Math.round(
    (Date.UTC(toYear, toMonth - 1, toDay) - Date.UTC(fromYear, fromMonth - 1, fromDay)) / 86400000,
  );
}

/**
 * The habit's lifecycle: it exists from its start date until the day it was
 * archived (inclusive). Mirrors `Habit#scheduled_on?`; the server rejects logs
 * outside it.
 */
export function isScheduledOn(habit: Habit, date: string): boolean {
  return date >= habit.startDate && (!habit.archivedAt || date <= habit.archivedAt.slice(0, 10));
}

export type SparklineDay = {
  date: string;
  /** `blank` covers both "not logged" and days outside the habit's lifecycle. */
  status: HabitLogStatus | 'blank';
  scheduled: boolean;
};

/** The `days` days ending at `endDate` (inclusive), oldest first. */
export function habitSparkline(
  habit: Habit,
  statusFor: HabitStatusLookup,
  endDate: string,
  days = SPARKLINE_DAYS,
): SparklineDay[] {
  return Array.from({ length: days }, (_, index) => {
    const date = shiftDate(endDate, index - (days - 1));
    const scheduled = isScheduledOn(habit, date);
    return { date, scheduled, status: (scheduled && statusFor(habit, date)) || 'blank' };
  });
}

export type HabitStreak = {
  count: number;
  /**
   * The run reaches the oldest day we have logs for, so it may be longer than
   * `count` (shown as "36+").
   */
  capped: boolean;
};

/**
 * Consecutive completed days ending at `endDate`. A day still to fill in
 * doesn't break the run: if `endDate` itself is blank the count starts from the
 * day before, so a streak survives until the day is over. A missed `endDate`
 * ends it.
 *
 * `oldestLoaded` is the first date whose logs are known; the walk stops there.
 */
export function habitStreak(
  habit: Habit,
  statusFor: HabitStatusLookup,
  endDate: string,
  oldestLoaded: string,
): HabitStreak {
  let date = endDate;
  if (!statusFor(habit, date) && isScheduledOn(habit, date)) date = shiftDate(date, -1);

  let count = 0;
  while (
    date >= oldestLoaded &&
    isScheduledOn(habit, date) &&
    statusFor(habit, date) === 'completed'
  ) {
    count += 1;
    date = shiftDate(date, -1);
  }
  // Stopped at the edge of the loaded logs, not at a gap or the habit's start.
  return { count, capped: count > 0 && date < oldestLoaded && isScheduledOn(habit, date) };
}

/** "3", or "36+" when the streak may be longer than the logs we have. */
export function formatStreak({ count, capped }: HabitStreak): string {
  return capped ? `${count}+` : String(count);
}
